import { useTranslation } from 'react-i18next';
import type { CargoProblemWorkspaceSystemCard } from '@magimp/shared';
import { Icon } from '../../../ui/index.js';
import { formatDateTime, type AppLocale } from '../../../i18n/index.js';
import { useCargoProblemLabels } from '../labels.js';
import { CargoProblemAttachmentTiles } from './CargoProblemAttachments.js';

/**
 * @aiInvariant a workflow note is rendered HERE and nowhere else. The stream
 *   never projects it as a second chat message, so the card is the single place
 *   the reason for a move is stated.
 */
export function CargoProblemSystemCard({
  problemId,
  item,
  locale,
}: {
  problemId: string;
  item: CargoProblemWorkspaceSystemCard;
  locale: AppLocale;
}) {
  const { t } = useTranslation('cargoProblems');
  const labels = useCargoProblemLabels();
  return (
    <article className="cargo-problem-system-card" data-testid="cargo-problem-system-card">
      <header>
        <Icon name="git-branch" size={14} />
        <strong>{labels.event(item.kind)}</strong>
        <span>{item.actor.name}</span>
        <time dateTime={item.createdAt}>{formatDateTime(item.createdAt, locale)}</time>
      </header>
      <p className="cargo-problem-system-consequence">{consequence()}</p>
      {'note' in item && item.note ? (
        <p className="cargo-problem-system-note">{item.note}</p>
      ) : null}
      {'attachments' in item ? (
        <CargoProblemAttachmentTiles problemId={problemId} files={item.attachments} />
      ) : null}
      {item.kind === 'FILE_UPLOADED' ? (
        <CargoProblemAttachmentTiles problemId={problemId} files={[item.file]} />
      ) : null}
    </article>
  );

  function consequence(): string {
    if (item.kind === 'ASSIGNEE_CHANGED') {
      return t('workspace.assigneeChange', {
        from: item.previousAssignee?.name ?? t('unassigned'),
        to: item.assignee.name,
      });
    }
    if (item.kind === 'ATTACHMENT_DELETED') {
      return t('workspace.attachmentDeleted', { reason: item.reason });
    }
    if ('from' in item) {
      return `${labels.status(item.from)} → ${labels.status(item.to)}`;
    }
    return t('workspace.systemCard');
  }
}
